/**
 * Microsoft Clarity Analytics Integration
 * 
 * Dynamically loads the Clarity tracking script with environment-specific project IDs.
 * Project IDs are configured per deployment environment via REACT_APP_INSIGHT_QORE_CLARITY_PROJECT_ID.
 */

let isInitialized = false;

/**
 * Initialize Microsoft Clarity tracking
 * 
 * This function dynamically injects the Clarity script into the page with the
 * project ID configured for the current environment. It only initializes once
 * per page load, even if called multiple times.
 * 
 * @returns {boolean} True if initialization succeeded, false otherwise
 */
function initialize() {
    // Prevent double initialization
    if (isInitialized) {
        console.warn('Clarity already initialized');
        return false;
    }

    // Get the project ID from environment variables
    const projectId = process.env.REACT_APP_INSIGHT_QORE_CLARITY_PROJECT_ID;

    // Skip initialization if no project ID is configured
    if (!projectId) {
        console.warn('Clarity project ID not configured. Set REACT_APP_INSIGHT_QORE_CLARITY_PROJECT_ID environment variable.');
        return false;
    }

    try {
        // Inject the Clarity tracking script
        // This is the same script provided by Microsoft Clarity
        (function (c, l, a, r, i, t, y) {
            c[a] = c[a] || function () {
                (c[a].q = c[a].q || []).push(arguments);
            };
            t = l.createElement(r);
            t.async = 1;
            t.src = 'https://www.clarity.ms/tag/' + i;
            y = l.getElementsByTagName(r)[0];
            y.parentNode.insertBefore(t, y);
        })(window, document, 'clarity', 'script', projectId);

        isInitialized = true;
        return true;
    } catch (error) {
        console.error('Failed to initialize Clarity:', error);
        return false;
    }
}

/**
 * Track a custom event in Clarity
 * 
 * @param {string} eventName - The name of the custom event
 */
function trackEvent(eventName) {
    if (!isInitialized || !window.clarity) {
        console.warn('Clarity not initialized. Cannot track event:', eventName);
        return;
    }

    try {
        window.clarity('event', eventName);
    } catch (error) {
        console.error('Failed to track Clarity event:', error);
    }
}

/**
 * Set a custom tag in Clarity
 * 
 * @param {string} key - The tag key
 * @param {string} value - The tag value
 */
function setTag(key, value) {
    if (!isInitialized || !window.clarity) {
        console.warn('Clarity not initialized. Cannot set tag:', key);
        return;
    }

    try {
        window.clarity('set', key, value);
    } catch (error) {
        console.error('Failed to set Clarity tag:', error);
    }
}

export default {
    initialize,
    trackEvent,
    setTag
};
